import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import {
  DialogWrapper,
  PrimeReactButton as ButtonAtom,
  SkeletonAtom,
  StatusBadgeAtom,
  useApiClientsOrNull,
} from 'design-system/my-lib';
import './OrganizationDomainJoinPanel.scss';

interface DomainJoinState {
  approved: boolean;
  domain: string | null;
  approvedAt: string | null;
  approvedByEmail: string | null;
  /** The org managers' switch; `null` when the service could not read it. */
  allowDomainJoin: boolean | null;
}

/** Why a change failed: one of the service's 409 refusal codes, or `failed` for anything else. */
type ChangeFailure = 'no_domain' | 'organization_not_ready' | 'failed';

const CHANGE_FAILURE_KEYS: Record<ChangeFailure, string> = {
  no_domain: 'admin.organizations.tray.domainJoin.noDomain',
  organization_not_ready: 'admin.organizations.tray.domainJoin.changeErrorNotReady',
  failed: 'admin.organizations.tray.domainJoin.changeErrorBody',
};

const toChangeFailure = (error: unknown): ChangeFailure => {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  const code = response?.status === 409 ? response.data?.code : undefined;
  return code === 'no_domain' || code === 'organization_not_ready' ? code : 'failed';
};

export interface OrganizationDomainJoinPanelProps {
  organizationSphereId: string;
  organizationName: string;
  domain: string | null;
}

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleDateString();
};

/**
 * Platform-admin control for an organization's domain auto-join approval, shown in the
 * organization list's expanded row. Reads and writes `/admin/organizations/:id/domain-join`.
 */
const OrganizationDomainJoinPanel: React.FC<OrganizationDomainJoinPanelProps> = ({
  organizationSphereId,
  organizationName,
  domain,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const organizationClient = apiClients?.organizationClient;
  const route = `/admin/organizations/${encodeURIComponent(organizationSphereId)}/domain-join`;

  const [state, setState] = useState<DomainJoinState | null>(null);
  const [loading, setLoading] = useState(Boolean(domain));
  const [loadFailed, setLoadFailed] = useState(false);
  const [changeFailure, setChangeFailure] = useState<ChangeFailure | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const triggerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!domain) return;
    if (!organizationClient) {
      setLoading(false);
      setLoadFailed(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    // Fire-and-forget: the body catches every error itself and reports it through state.
    void (async () => {
      try {
        const response = await organizationClient.get<DomainJoinState>(route);
        if (!cancelled) setState(response);
      } catch {
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [domain, organizationClient, route, reloadToken]);

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false);
    // The dialog unmounts its own focus trap; hand focus back to the control that opened it.
    requestAnimationFrame(() => triggerRef.current?.querySelector('button')?.focus());
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!organizationClient || !state) return;
    setBusy(true);
    setChangeFailure(null);
    try {
      const next = await organizationClient.put<DomainJoinState>(route, { approved: !state.approved });
      setState(next);
      closeConfirm();
    } catch (error) {
      const failure = toChangeFailure(error);
      setChangeFailure(failure);
      closeConfirm();
      // Only a refusal is known to have changed nothing; after any other failure the change may
      // have landed, so the panel re-reads the approval rather than show what it assumed.
      if (failure === 'failed') setReloadToken((token) => token + 1);
    } finally {
      setBusy(false);
    }
  }, [organizationClient, state, route, closeConfirm]);

  const title = <h4 className="org-domain-join__title">{t('admin.organizations.tray.domainJoin.title')}</h4>;

  if (!domain) {
    return (
      <section className="org-domain-join" data-testid="org-domain-join">
        {title}
        <p className="org-domain-join__note" data-testid="org-domain-join-no-domain">
          {t('admin.organizations.tray.domainJoin.noDomain')}
        </p>
      </section>
    );
  }

  if (loading) {
    return (
      <section
        className="org-domain-join"
        data-testid="org-domain-join"
        aria-busy="true"
        aria-label={t('admin.organizations.tray.domainJoin.loading')}
      >
        {title}
        <SkeletonAtom count={2} height="1.25rem" />
      </section>
    );
  }

  if (loadFailed || !state) {
    return (
      <section className="org-domain-join" data-testid="org-domain-join">
        {title}
        <div className="org-domain-join__error" role="alert" data-testid="org-domain-join-error">
          <strong>{t('admin.organizations.tray.domainJoin.errorTitle')}</strong>
          <span>{t('admin.organizations.tray.domainJoin.errorBody')}</span>
          <ButtonAtom
            label={t('admin.organizations.tray.domainJoin.retry')}
            icon="pi pi-refresh"
            variant="secondary"
            size="small"
            onClick={() => setReloadToken((token) => token + 1)}
          />
        </div>
      </section>
    );
  }

  const approving = !state.approved;
  const dialogKey = approving ? 'Grant' : 'Revoke';
  const approvedDetail = state.approvedByEmail
    ? t('admin.organizations.tray.domainJoin.approvedBy', {
        email: state.approvedByEmail,
        date: formatDate(state.approvedAt),
      })
    : t('admin.organizations.tray.domainJoin.approvedAt', { date: formatDate(state.approvedAt) });

  return (
    <section className="org-domain-join" data-testid="org-domain-join">
      {title}
      <div className="org-domain-join__row">
        <div className="org-domain-join__status">
          <StatusBadgeAtom
            status={state.approved ? 'active' : 'deactivated'}
            showIcon
            label={
              state.approved
                ? t('admin.organizations.tray.domainJoin.approved', { domain })
                : t('admin.organizations.tray.domainJoin.notApproved')
            }
          />
          {state.approved && <span className="org-domain-join__detail">{approvedDetail}</span>}
        </div>
        <span ref={triggerRef}>
          <ButtonAtom
            label={t(`admin.organizations.tray.domainJoin.${approving ? 'grant' : 'revoke'}`)}
            icon={approving ? 'pi pi-check' : 'pi pi-times'}
            variant={approving ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setConfirmOpen(true)}
          />
        </span>
      </div>
      {state.allowDomainJoin === false && (
        <p className="org-domain-join__note">{t('admin.organizations.tray.domainJoin.managersOff')}</p>
      )}
      {state.allowDomainJoin === null && (
        <p className="org-domain-join__note" data-testid="org-domain-join-managers-unknown">
          {t('admin.organizations.tray.domainJoin.managersUnknown')}
        </p>
      )}
      {changeFailure && (
        <div className="org-domain-join__error" role="alert" data-testid="org-domain-join-change-error">
          <strong>{t('admin.organizations.tray.domainJoin.changeErrorTitle')}</strong>
          <span>{t(CHANGE_FAILURE_KEYS[changeFailure])}</span>
        </div>
      )}
      <DialogWrapper
        header={t(`admin.organizations.tray.domainJoin.confirm${dialogKey}Title`)}
        visible={confirmOpen}
        onHide={closeConfirm}
        style={{ width: '480px', maxWidth: '95vw', minHeight: 'auto' }}
      >
        <div className="org-domain-join__dialog">
          <p data-testid="org-domain-join-confirm-body">
            {t(`admin.organizations.tray.domainJoin.confirm${dialogKey}Body`, {
              domain,
              organization: organizationName,
            })}
          </p>
          <div className="org-domain-join__dialog-actions">
            <ButtonAtom
              label={t('admin.organizations.tray.domainJoin.cancel')}
              variant="text"
              onClick={closeConfirm}
              disabled={busy}
            />
            <ButtonAtom
              label={t(`admin.organizations.tray.domainJoin.confirm${dialogKey}`)}
              variant={approving ? 'primary' : 'danger'}
              loading={busy}
              onClick={handleConfirm}
              autoFocus
            />
          </div>
        </div>
      </DialogWrapper>
    </section>
  );
};

export default OrganizationDomainJoinPanel;
